/**
 * Document helpers for prosemirror-editor — Phase 6 replacement.
 * Delegates to /api/reports/* on dash-api.
 */

import {headerUpdatesToApiMeta} from '../../../utils/documentHeaderMeta';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${BASE}${path}`, {headers: authHeaders(), ...opts});
    if (resp.status === 404) return null;
    if (!resp.ok) throw new Error(`${opts.method || 'GET'} ${path} → ${resp.status}`);
    if (resp.status === 204) return null;
    return resp.json();
}

export const subscribeToUserDocuments = (userId, onNext, onError) => {
    if (!userId) { onNext([]); return () => {}; }
    let cancelled = false;
    apiFetch(`/api/reports?userId=${encodeURIComponent(userId)}`)
        .then((data) => { if (!cancelled) onNext(Array.isArray(data) ? data : []); })
        .catch((err) => { if (!cancelled) onError?.(err); });
    return () => { cancelled = true; };
};

export const createNewDocumentForUser = async (userId) => {
    if (!userId) throw new Error('Cannot create document without userId');
    const now = new Date().toISOString();
    const doc = {
        owner: userId,
        title: 'Untitled document',
        createdAt: now,
        lastUpdated: now,
        meta: {
            dtg: '',
            originator: {value: '', label: 'Select originator'},
            classification: {value: 'TS', label: 'TS - Top Secret'},
            distribution: [],
            compartment: [],
            confidence: {value: '', label: 'Select confidence'},
            ifc: {value: '', label: 'Select IFC'},
            productionCenter: {value: '', label: 'Select production center'},
            authors: []
        }
    };
    return apiFetch('/api/reports', {method: 'POST', body: JSON.stringify(doc)});
};

export const subscribeToDocument = (documentId, onNext, onError) => {
    if (!documentId) { onNext(null); return () => {}; }
    let cancelled = false;
    apiFetch(`/api/reports/${encodeURIComponent(documentId)}`)
        .then((data) => { if (!cancelled) onNext(data); })
        .catch((err) => { if (!cancelled) onError?.(err); });
    return () => { cancelled = true; };
};

export const updateDocumentHeaderData = async (documentId, updates) => {
    if (!documentId) throw new Error('Cannot update document without documentId');
    const meta = headerUpdatesToApiMeta(updates);
    if (!meta || Object.keys(meta).length === 0) {
        return;
    }
    await apiFetch(`/api/reports/${encodeURIComponent(documentId)}`, {
        method: 'PUT',
        body: JSON.stringify({meta, lastUpdated: new Date().toISOString()}),
    });
};

export const updateDocumentTitle = async (documentId, title) => {
    if (!documentId) throw new Error('Cannot update document without documentId');
    await apiFetch(`/api/reports/${encodeURIComponent(documentId)}`, {
        method: 'PUT',
        body: JSON.stringify({title, lastUpdated: new Date().toISOString()}),
    });
};

export const getDocumentById = async (documentId) => {
    if (!documentId) return null;
    return apiFetch(`/api/reports/${encodeURIComponent(documentId)}`);
};
